import I18n from 'I18n';
import getLangLocale from 'I18n/utils/getLangLocale';
import { createSelector } from 'reselect';
import { getIsGlobalGroupOrPartial, getIsSection
// @ts-expect-error not yet migrated
} from 'ContentEditorUI/components/sidebar/contentTree/utils';
import { TEXT_BASED_MODULES } from 'ContentEditorUI/components/sidebar/contentTree/constants';
import { getTypeByModuleId, getModules, getAllLayoutSectionTreesAsMap, getModuleGroups, getPostBodyModulesAsLayoutTree } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getModuleDisplayName } from 'ContentUtils/helpers/ModuleHelpers';
// @ts-expect-error not yet migrated
import { findModuleById } from 'ContentEditorUI/data/moduleUtils';
import { getBuiltInTypesByModuleId, getModuleSchemas, getSchemaForModuleHelper, getModuleIdsByBuiltinType, getAllModuleSchemasAsArray } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { getSidebarState } from 'ContentEditorUI/redux/selectors/sidebarSelectors';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';

// @ts-expect-error Untyped module

// @ts-expect-error not yet migrated

import { getImmutableOrPlain, isImmutable } from 'ContentEditorUI/utils/dataHelpers';
const defaultNames = {
  moduleDisplayLabel: '',
  textContents: '',
  layoutDisplayLabel: '',
  layoutDefaultLabel: ''
};
/**
 * get full "faked" state object we passed to memoized selectors
 * the faked state is a subset of the global state structured
 * for easy consumption by the memoized selectors.
 *
 * See FakeState type for the structure of the faked state.
 */
const getFakedState = state => state;
const getModuleById = (state, moduleId) => findModuleById(state.modules, moduleId);
const getModuleGroupById = (state, moduleGroupId) => {
  return state.moduleGroups.get(moduleGroupId);
};
const getTreeData = (state, layoutSectionName) => getImmutableOrPlain(state.allLayoutSectionTreesAsMap, layoutSectionName).getRootCell();
const makeGetModuleMatchableStrings = (moduleId, names = Object.assign({}, defaultNames)) => {
  return createSelector([state => getModuleById(state, moduleId),
  // Casting these selectors to FakeStateSelector since the their state
  // types don't match the FakeState type. This should still be accurate as
  // we're actually passing in a FakeState object to the selectors vs
  // their original state type.
  getAllModuleSchemasAsArray, getModuleIdsByBuiltinType, getBuiltInTypesByModuleId, state => state.categoryId], (currentModule, allModuleSchemasAsArray, moduleIdsByBuiltinType, builtInTypesByModuleId, categoryId) => {
    const schema = getSchemaForModuleHelper(currentModule, allModuleSchemasAsArray, moduleIdsByBuiltinType, categoryId);
    const typeModuleId = currentModule ? currentModule.get('module_id') : null;
    const moduleDisplayLabel = currentModule.get('label') ||
    // @ts-expect-error Argument of type 'ModuleSchema | null | undefined' is not assignable to parameter of type 'ModuleSchema | undefined'.
    getModuleDisplayName(currentModule, getLangLocale(), schema);
    const moduleType = getTypeByModuleId(typeModuleId, builtInTypesByModuleId);
    names.moduleDisplayLabel = moduleDisplayLabel;
    if (TEXT_BASED_MODULES.includes(moduleType)) {
      if (names.moduleDisplayLabel === schema.label) {
        if (moduleType === 'rich_text') {
          const bodyHtml = currentModule.getIn(['body', 'html']);
          const html = bodyHtml || schema.fields[0].default;
          /**
           * Using DOMParser here as we can't guarantee that the content of a
           * customer's rich text is safe and shouldn't be rendering it directly
           * in the parent app.
           *
           * Context: https://issues.hubspotcentral.com/browse/HMSP-10346
           */
          const parser = new DOMParser();
          const safeDocument = parser.parseFromString(html, 'text/html');
          names.textContents = safeDocument.body.innerText;
        } else {
          const moduleValue = currentModule.getIn(['body', 'value']);
          names.textContents = moduleValue || schema.fields[0].default;
        }
      } else {
        names.textContents = '';
      }
    }
    return names;
  });
};
export const makeGetModulesMatchableStrings = moduleGroupId => {
  const memoizedSelectors = {};
  return createSelector([getFakedState, state => getModuleGroupById(state, moduleGroupId)], (fakedState, moduleGroup) => {
    const results = {};
    const {
      modules
    } = moduleGroup;
    const buildModuleMatchableStrings = module => {
      let memoizedGetStrings;
      const isGlobal = getIsGlobalGroupOrPartial(module);
      const moduleId = isGlobal ? module.get('path') : module.get('id');
      if (!memoizedSelectors[moduleId]) {
        if (isGlobal || moduleId === undefined) {
          memoizedGetStrings = () => {
            return {
              moduleDisplayLabel: module.get('label')
            };
          };
        } else {
          memoizedGetStrings = makeGetModuleMatchableStrings(moduleId);
        }
        memoizedSelectors[moduleId] = memoizedGetStrings;
      } else {
        memoizedGetStrings = memoizedSelectors[moduleId];
      }
      results[moduleId] = {
        names: memoizedGetStrings(fakedState)
      };
    };
    let moduleValues;
    if (isImmutable(modules)) {
      moduleValues = modules.valueSeq();
    } else if (modules instanceof Map) {
      moduleValues = [...modules.values()];
    } else {
      moduleValues = Object.values(modules);
    }
    moduleValues.forEach(module => {
      if (Array.isArray(module)) {
        module.forEach(nestedModule => {
          buildModuleMatchableStrings(nestedModule);
        });
      } else {
        buildModuleMatchableStrings(module);
      }
    });
    return results;
  });
};
export const getDndAreasThatShowAllRows = createSelector([getSidebarState], sidebarState => sidebarState.dndAreasThatShowAllRows);
export const makeGetMatchableStringsForTreeNode = layoutSectionName => {
  const memoizedSelectors = {};
  return createSelector([getFakedState, state => getTreeData(state, layoutSectionName)], (fakeState, treeData) => {
    const results = {};
    const nodeQueue = [];
    nodeQueue.push(treeData);
    while (nodeQueue.length > 0) {
      let names = Object.assign({}, defaultNames);
      const currentNode = nodeQueue.shift();
      const id = currentNode.getName();
      const nodeValue = currentNode.getValue();
      const isCell = currentNode.isCell();
      const nodeParams = isCell && currentNode.getParams() || {};
      const isModule = currentNode.isModule() || nodeParams.isModuleParent;
      const isColumn = isCell && !isModule;
      const isRow = currentNode.isRow();
      const isSection = getIsSection(currentNode);
      const isEmbed = currentNode.isEmbed();
      const isStaticSection = currentNode.isStaticSection();
      if (isModule) {
        let memoizedGetStrings;
        if (!memoizedSelectors[id]) {
          memoizedGetStrings = makeGetModuleMatchableStrings(id, names);
          memoizedSelectors[id] = memoizedGetStrings;
        } else {
          memoizedGetStrings = memoizedSelectors[id];
        }
        names = memoizedGetStrings(fakeState);
      } else if (isEmbed) {
        names.layoutDisplayLabel = nodeValue && nodeValue.label;
        names.layoutDisplayLabel = I18n.text('components.sidebar.treeView.fragmentLabels.embed');
      } else if (isStaticSection) {
        names.layoutDisplayLabel = nodeValue && nodeValue.label;
        names.layoutDefaultLabel = I18n.text('components.sidebar.treeView.fragmentLabels.fixedLayoutSection');
      } else if (isSection) {
        names.layoutDisplayLabel = nodeValue && nodeValue.label;
        names.layoutDefaultLabel = I18n.text('components.sidebar.treeView.fragmentLabels.section');
      } else if (isColumn) {
        names.layoutDisplayLabel = nodeValue && nodeValue.label;
        names.layoutDefaultLabel = I18n.text('components.sidebar.treeView.fragmentLabels.column');
      } else if (isRow) {
        names.layoutDisplayLabel = nodeValue && nodeValue.label;
        names.layoutDefaultLabel = I18n.text('components.sidebar.treeView.fragmentLabels.row');
      } else {
        throw new Error('Unknown Node in Layout Tree');
      }
      const parentNames = [];
      let parentNode = currentNode.getParent();
      while (parentNode) {
        parentNames.push(parentNode.getName());
        parentNode = parentNode.getParent();
      }
      let children = isCell ? currentNode.getRows() : currentNode.getColumns();
      if (currentNode.isStaticSection()) {
        children = currentNode.getModuleChildren();
      }
      const skipNode = !fakeState.dndAreasThatShowAllRows.has(layoutSectionName) && (isRow && children.length === 1 && !isSection || id === currentNode.getRootName());
      if (!skipNode) {
        results[id] = {
          names,
          parentNames
        };
      }
      if (children) {
        const reversedChildren = children.reverse();
        reversedChildren.forEach(child => {
          nodeQueue.unshift(child);
        });
      }
    }
    return results;
  });
};
function setOnObject(obj, key, value) {
  if (isImmutable(obj)) {
    // @ts-expect-error We know that T is an immutable here
    return obj.set(key, value);
  }
  return Object.assign({}, obj, {
    [key]: value
  });
}
const makeGetAllMatchableStrings = () => {
  const memoizedSelectors = {};
  return createSelector([getModuleGroups, getModuleSchemas, getModules, getAllLayoutSectionTreesAsMap, getDndAreasThatShowAllRows, getPostBodyModulesAsLayoutTree, getCategoryId], (moduleGroups, moduleSchemas, allModules, allLayoutSectionTreesAsMap, dndAreasThatShowAllRows, postBodyModulesLayoutTree, categoryId) => {
    const fakedState = {
      moduleGroups,
      moduleSchemas,
      modules: allModules,
      categoryId,
      allLayoutSectionTreesAsMap: postBodyModulesLayoutTree ? setOnObject(allLayoutSectionTreesAsMap, postBodyModulesLayoutTree.getRootName(), postBodyModulesLayoutTree) : allLayoutSectionTreesAsMap,
      dndAreasThatShowAllRows
    };
    const matchableStrings = {};
    [...moduleGroups.entries()].forEach(([id, group]) => {
      let memoizedGetStrings;
      const isLayoutSection = group.type === 'layout_section';
      const memoizedSelector = isLayoutSection ? makeGetMatchableStringsForTreeNode : makeGetModulesMatchableStrings;
      if (!memoizedSelectors[id]) {
        memoizedGetStrings = memoizedSelector(id);
        memoizedSelectors[id] = memoizedGetStrings;
      } else {
        memoizedGetStrings = memoizedSelectors[id];
      }
      matchableStrings[id] = memoizedGetStrings(fakedState);
    });
    return matchableStrings;
  });
};
export const getAllMatchableStrings = makeGetAllMatchableStrings();
export const getClosedTreeNodes = createSelector([getSidebarState], sidebarState => sidebarState.closedTreeNodes);
export const isTreeNodeOpen = (state, nodeId) => {
  const closedTreeNodes = getClosedTreeNodes(state);
  return !closedTreeNodes.has(nodeId);
};
export const getSearchTerm = createSelector([getSidebarState], sidebarState => sidebarState.sidebarSearchTerm);
export const getActiveMatch = createSelector([getSidebarState], sidebarState => sidebarState.sidebarActiveMatch);
export const getMatchIds = createSelector([getSidebarState], sidebarState => sidebarState.sidebarMatchIds);
export const getOpenDropdownNodeId = createSelector([getSidebarState], sidebarState => sidebarState.openExpandableMenuNodeId);
export const getSidebarScrollTop = createSelector([getSidebarState], sidebarState => sidebarState.sidebarScrollTop);